import { formatDecimal } from 'framework/utils';
import { calculateEnhancedTeamStructure, ProjectTeamType } from 'domain/shared/newProject';
import { StaffingAssignment, StaffingAssignmentListMember, StaffingAssignmentSlot } from 'domain/shared/staffing';
import { ASSIGNMENTS_STATUS } from './constants';

type StaffingProjectWithAssignments = {
  budget?: {
    fte?: string;
    billingType?: string;
  };
  assignments?: StaffingAssignment[];
};

export const mapTeamStructureWithAssignments = (
  projectData: StaffingProjectWithAssignments,
  team?: ProjectTeamType,
): { team: Omit<Partial<ProjectTeamType>, 'expectedStructure'> & { expectedStructure: StaffingAssignmentSlot[] } } => {
  const enhancedTeam = calculateEnhancedTeamStructure(
    team,
    projectData?.budget?.fte,
    projectData?.budget?.billingType,
  ).team;
  const assignments = projectData?.assignments || [];

  const currentDate = new Date();

  return {
    team: {
      ...enhancedTeam,
      expectedStructure: (enhancedTeam?.expectedStructure || []).map(slot => {
        const members = assignments
          .filter(assignment => assignment.slotId === slot.id)
          .map(assignment => {
            const issues: string[] = [];

            if (slot.competencyLevel && assignment.competencyLevel !== slot.competencyLevel) {
              issues.push('Level mismatch');
            }
            if (slot.positionId && assignment.positionId !== slot.positionId) {
              issues.push('Position mismatch');
            }

            const isFuture = assignment.fromDate && new Date(assignment.fromDate) > currentDate;

            return {
              id: assignment.id,
              assignmentId: assignment.id,
              slotId: assignment.slotId ?? null,
              employeeAlias: assignment.employeeAlias,
              employeeId: assignment.employeeAlias,
              positionId: assignment.positionId ?? null,
              technologyId: assignment.technologyId ? String(assignment.technologyId) : null,
              competencyLevel: assignment.competencyLevel ?? null,
              fte: Number(assignment.maxPercent || 0) / 100,
              minPercent: Number(assignment.minPercent || 0),
              maxPercent: Number(assignment.maxPercent || 0),
              role: assignment.membershipRole ?? null,
              membershipRole: assignment.membershipRole,
              isProbation: assignment.isProbation || false,
              isBackup: assignment.isBackup || false,
              isSpof: assignment.isSpof ?? false,
              isBench: assignment.isBench || false,
              backup: assignment.isBackup || false,
              bench: assignment.isBench || false,
              startDate: assignment.fromDate || '',
              endDate: assignment.toDate || '',
              from: assignment.fromDate,
              to: assignment.toDate,
              noteText: assignment.noteText ?? null,
              issues,
              isPending: assignment.status === ASSIGNMENTS_STATUS.PENDING,
              status: isFuture ? 'FUTURE' : 'ACTIVE',
            } satisfies StaffingAssignmentListMember;
          });

        const assignedFte = members
          .filter(member => !member.isBench && !member.isBackup)
          .reduce((sum, member) => sum + (Number(member.fte) || 0), 0);
        const issues: string[] = [];

        if (assignedFte < Number(slot.requiredFte || 0)) {
          issues.push(`${formatDecimal(Number(slot.requiredFte || 0) - assignedFte)} FTE understaffed`);
        }
        if (assignedFte > Number(slot.requiredFte || 0)) {
          issues.push(`${formatDecimal(assignedFte - Number(slot.requiredFte || 0))} FTE overstaffed`);
        }

        return {
          ...slot,
          members,
          issues,
          assignedFte,
        };
      }),
    },
  };
};
